var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef, useCallback } from "react";
import cx from "classnames";
import "./Button.scss";
import { Typography } from "../typography/Typography";
import { Icon } from "../icons/Icon";
function ButtonFunction(_a, forwardedRef) {
    var { type = "button", onClick, className, active, disabled, block, inline, ariaLabel, "data-test-id": dataTestId, tabIndex, size = "medium", variant = "primary", component, label, icon } = _a, props = __rest(_a, ["type", "onClick", "className", "active", "disabled", "block", "inline", "ariaLabel", "data-test-id", "tabIndex", "size", "variant", "component", "label", "icon"]);
    const [isAnimating, setIsAnimating] = useState(false);
    const internalRef = useRef(null);
    const timeout = useRef(null);
    const isCircleButton = className === null || className === void 0 ? void 0 : className.includes("button-circle");
    const classes = cx(className, "lysa-ui-button", "lysa-ripple-box", `button-${variant}`, { active: active }, { disabled: disabled }, { block: block }, { inline: inline }, { "is-animating": isAnimating }, {
        [`button-${size}`]: !icon && !isCircleButton,
    }, {
        [`button-${size}-icon`]: icon && label && !isCircleButton,
    }, {
        [`button-${size}-icon-only`]: icon && !label && !isCircleButton,
    });
    const IconComponent = icon && Icon[icon];
    const Component = component || "button";
    const activeRef = forwardedRef || internalRef;
    useEffect(() => {
        return () => {
            if (timeout && timeout.current) {
                clearTimeout(timeout.current);
            }
        };
    }, []);
    useEffect(() => {
        const savedRef = activeRef;
        if (isAnimating && savedRef && savedRef.current) {
            savedRef.current.addEventListener("animationend", () => setIsAnimating(false));
        }
        else if (savedRef && savedRef.current) {
            savedRef.current.removeEventListener("animationend", () => setIsAnimating(false));
        }
        return () => {
            if (savedRef && savedRef.current) {
                savedRef.current.removeEventListener("animationend", () => setIsAnimating(false));
            }
        };
    }, [isAnimating, activeRef]);
    const onButtonClick = useCallback((event) => {
        var _a;
        if (disabled) {
            return;
        }
        setIsAnimating(false);
        if ((_a = activeRef === null || activeRef === void 0 ? void 0 : activeRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect) {
            const offsets = activeRef.current.getBoundingClientRect();
            const xInButton = event.clientX - offsets.left > 0 ? event.clientX - offsets.left : 0;
            const yInButton = event.clientY - offsets.top > 0 ? event.clientY - offsets.top : 0;
            activeRef.current.style.setProperty("--x", xInButton.toString());
            activeRef.current.style.setProperty("--y", yInButton.toString());
        }
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
        timeout.current = setTimeout(() => {
            var _a;
            void ((_a = activeRef === null || activeRef === void 0 ? void 0 : activeRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth);
            setIsAnimating(true);
        }, 0);
    }, [activeRef, disabled, onClick]);
    return (React.createElement(Component, Object.assign({ type: Component === "button" ? type : undefined, ref: activeRef, onClick: onButtonClick, className: classes, "data-test-id": dataTestId, "aria-label": ariaLabel, tabIndex: tabIndex }, props),
        IconComponent && (React.createElement(IconComponent, { size: size === "small" && !isCircleButton ? 16 : 20 })),
        label && React.createElement(Typography, { type: "label" }, label),
        !disabled && React.createElement("span", { className: "lysa-ripple" })));
}
export const Button = React.forwardRef(ButtonFunction);
